// @flow

import type { State } from "@store/reducers";
import type {
  TrendActivity,
  DepartmentOverview,
  ToolsDashboardStatisticsItem,
  ReasonOverview,
} from "@modules/reporting/reducer/dashboard/types";
import type { ValuesItemValue } from "@modules/reporting/reducer/types";
import type { EffectiveParameters } from "@modules/reporting/reducer/focus-lists/department/types";
import { isLoading as isAbsenceDetailsLoading } from "./reports/absence-details";
import { isLoading as isAbsenceMonitoringLoading } from "./reports/absence-monitoring";
import { isLoading as isAlertDetailsLoading } from "./reports/alert-details";
import { isLoading as isReferralDetailsLoading } from "./reports/referral-details";
import { isLoading as isRTWDetailsLoading } from "./reports/rtw-details";
import { isLoading as isEmployeeReportsLoading } from "./focus-lists/employee/reports";
import { isLoading as isDepartmentReportsLoading } from "./focus-lists/department/reports";
import { isLoading as isManagerReportsLoading } from "./focus-lists/manager/reports";
import { isLoading as isEmployeeReportLoading } from "./focus-lists/employee/report";
import { isLoading as isDepartmentReportLoading } from "./focus-lists/department/report";
import { isLoading as isManagerReportLoading } from "./focus-lists/manager/report";
import { isLoading as isAbsenceRateLoading } from "./trends/absence-rate";
import { isLoading as isAbsenceReasonLoading } from "./trends/absence-reason";
import { isLoading as isComplianceLoading } from "./trends/compliance";
import { isLoading as isReferralsReportLoading } from "./referral-tracker";
import { isLoading as isEmployeeDetailsLoading } from "./employee/employee-details";

export type GetTrendActivity = (state: State) => TrendActivity[];
export type GetDepartmentOverview = (state: State) => DepartmentOverview[];
export type GetToolsDashboardNotificationReviews = (state: State) => ValuesItemValue[];
export type GetToolsDashboardStatistics = (state: State) => ToolsDashboardStatisticsItem[];
export type GetToolsDashboardToday = (state: State) => ValuesItemValue[];
export type GetEffectiveParameters = (state: State) => EffectiveParameters;
export type GetReasonOverview = (state: State) => ReasonOverview[];

export const isLoading = (state: State): boolean => {
  const dashboardRequests = state.reporting.dashboard.requestsInProgress;
  const absencesRequests = state.reporting.reports.absenceDetails.requestsInProgress;
  const userRequests = state.reporting.user.requestsInProgress;

  if (dashboardRequests > 0 || absencesRequests > 0 || userRequests > 0) {
    return true;
  }

  return false;
};

export const getTrendActivity = (state: State): TrendActivity[] =>
  ((((state || {}).reporting || {}).dashboard || {}).absenceNow || {}).trendActivity || [];

export const getDepartmentOverview = (state: State): DepartmentOverview[] =>
  ((((state || {}).reporting || {}).dashboard || {}).absenceNow || {}).departmentOverview || [];

export const getEffectiveParameters = (state: State): ?EffectiveParameters =>
  state.reporting.dashboard.absenceNow.effectiveParameters;

export const getToolsDashboardNotificationReviews = (state: State): ValuesItemValue[] =>
  ((((state || {}).reporting || {}).dashboard || {}).overview || {}).notificationReviews || [];

export const getToolsDashboardStatistics = (state: State): ToolsDashboardStatisticsItem[] =>
  ((((state || {}).reporting || {}).dashboard || {}).overview || {}).statistics || [];

export const getToolsDashboardToday = (state: State): ValuesItemValue[] =>
  ((((state || {}).reporting || {}).dashboard || {}).overview || {}).today || [];

export const getTargetedReasons = (state: State) => {
  return state.reporting.dashboard.overview.targetedReasons;
};

export const getReasonOverview = (state: State): ReasonOverview[] =>
  ((((state || {}).reporting || {}).dashboard || {}).absenceNow || {}).reasonOverview || [];

export const getIsReportLoading: (state: State) => boolean = state =>
  isAbsenceDetailsLoading(state) ||
  isAbsenceMonitoringLoading(state) ||
  isAlertDetailsLoading(state) ||
  isReferralDetailsLoading(state) ||
  isReferralsReportLoading(state) ||
  isEmployeeDetailsLoading(state) ||
  isRTWDetailsLoading(state) ||
  isEmployeeReportsLoading(state) ||
  isDepartmentReportsLoading(state) ||
  isManagerReportsLoading(state) ||
  isEmployeeReportLoading(state) ||
  isDepartmentReportLoading(state) ||
  isManagerReportLoading(state) ||
  isAbsenceRateLoading(state) ||
  isAbsenceReasonLoading(state) ||
  isComplianceLoading(state);

export const getLoggedUserDashboardToday = (state: State): ValuesItemValue[] => {
  return state.reporting.user.overview.today;
};
